import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Search, Edit, Add, FilterList, Delete, Share } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Compass, PaginatedResponse } from '../types';
import { apiService } from '../services/api';

const CompassListPage: React.FC = () => {
  const [compasses, setCompasses] = useState<Compass[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'submitted'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; compass: Compass | null }>({
    open: false,
    compass: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [shareDialog, setShareDialog] = useState<{ open: boolean; compass: Compass | null }>({
    open: false,
    compass: null,
  });
  const [shareEmails, setShareEmails] = useState('');
  const [sharing, setSharing] = useState(false);
  const navigate = useNavigate();

  const pageSize = 12;

  useEffect(() => {
    loadCompasses();
  }, [currentPage, statusFilter]);

  const loadCompasses = async () => {
    try {
      setLoading(true);
      let response: PaginatedResponse<Compass>;
      
      if (statusFilter === 'draft') {
        const drafts = await apiService.getDraftCompasses();
        response = {
          success: true,
          data: drafts,
          pagination: {
            page: 1,
            limit: drafts.length,
            total: drafts.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          },
        };
      } else if (statusFilter === 'submitted') {
        response = await apiService.getSubmittedCompasses(currentPage, pageSize);
      } else {
        response = await apiService.getCompasses(currentPage, pageSize);
      }

      setCompasses(response.data || []);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.total);
    } catch (error) {
      console.error('Failed to load compasses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompasses = compasses.filter(compass =>
    searchTerm === '' || 
    compass.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    compass.what_happened?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleDeleteClick = (compass: Compass) => {
    setDeleteDialog({ open: true, compass });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, compass: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.compass) return;

    try {
      setDeleting(true);
      await apiService.deleteCompass(deleteDialog.compass.id);
      setDeleteDialog({ open: false, compass: null });
      // Refresh the compass list
      await loadCompasses();
    } catch (error) {
      console.error('Failed to delete compass:', error);
      // You might want to show an error message to the user here
    } finally {
      setDeleting(false);
    }
  };

  const handleShareClick = (compass: Compass) => {
    setShareDialog({ open: true, compass });
  };

  const handleShareCancel = () => {
    setShareDialog({ open: false, compass: null });
    setShareEmails('');
  };

  const handleShareConfirm = async () => {
    if (!shareDialog.compass || !shareEmails.trim()) return;

    try {
      setSharing(true);
      const emails = shareEmails.split(',').map(email => email.trim()).filter(email => email);
      await apiService.shareCompass(shareDialog.compass.id, {
        emails,
        permission_type: 'read'
      });
      setShareDialog({ open: false, compass: null });
      setShareEmails('');
      // You might want to show a success message here
    } catch (error) {
      console.error('Failed to share compass:', error);
      // You might want to show an error message to the user here
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem' },
          lineHeight: 1.2
        }}>
          My Compasses ({totalCount})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/compass/new')}
          sx={{
            minHeight: 44, // Enhanced touch target for mobile
            alignSelf: { xs: 'stretch', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' }
          }}
        >
          New Compass
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        gap: 2, 
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          placeholder="Search compasses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ 
            minWidth: { xs: '100%', sm: 300 },
            flex: { xs: 'none', sm: 1 }
          }}
        />
        
        <FormControl sx={{ 
          minWidth: { xs: '100%', sm: 150 },
          flex: { xs: 'none', sm: 'none' }
        }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
            startAdornment={<FilterList sx={{ mr: 1 }} />}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Drafts</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredCompasses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No compasses match your search' : 'No compasses found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm 
              ? 'Try adjusting your search terms or filters'
              : 'Create your first compass to start your accountability journey'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/compass/new')}
          >
            Create New Compass
          </Button>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {filteredCompasses.map((compass) => (
              <Grid item xs={12} sm={6} md={4} key={compass.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 1, mr: 1 }}>
                        {compass.title || 'Untitled'}
                      </Typography>
                      <Chip 
                        label={compass.status} 
                        size="small" 
                        color={compass.status === 'submitted' ? 'success' : 'warning'} 
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Created: {formatDate(compass.created_at)}
                    </Typography>
                    
                    {compass.updated_at !== compass.created_at && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Updated: {formatDate(compass.updated_at)}
                      </Typography>
                    )}

                    {compass.what_happened && (
                      <Typography variant="body2" sx={{ 
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {compass.what_happened}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/compass/edit/${compass.id}`)}
                      >
                        {compass.status === 'draft' ? 'Continue' : 'View'}
                      </Button>
                      {compass.status === 'submitted' && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleShareClick(compass)}
                          sx={{ ml: 1 }}
                        >
                          <Share />
                        </IconButton>
                      )}
                      {compass.status === 'draft' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(compass)}
                          sx={{ ml: 1 }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Draft Compass
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the compass "{deleteDialog.compass?.title || 'Untitled'}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Compass Dialog */}
      <Dialog
        open={shareDialog.open}
        onClose={handleShareCancel}
        aria-labelledby="share-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="share-dialog-title">
          Share Compass: {shareDialog.compass?.title || 'Untitled'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter email addresses (separated by commas) to share this compass with others. 
            They will be able to view the compass and add notes.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email addresses"
            fullWidth
            variant="outlined"
            value={shareEmails}
            onChange={(e) => setShareEmails(e.target.value)}
            placeholder="user1@example.com, user2@example.com"
            helperText="Separate multiple email addresses with commas"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareCancel} disabled={sharing}>
            Cancel
          </Button>
          <Button 
            onClick={handleShareConfirm} 
            variant="contained"
            disabled={sharing || !shareEmails.trim()}
            startIcon={sharing ? <CircularProgress size={16} /> : <Share />}
          >
            {sharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompassListPage;
